import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ShoppingBagRoundedIcon from "@mui/icons-material/ShoppingBagRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useParams } from "react-router-dom";
import { customersApi } from "../../api/customers";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import {
  hasProjectPermission,
  normalizeProjectAccess,
} from "../../utils/projectAccess";

const BRAND = { orange: "#F35A3A", dark: "#19242F", bg: "#F6F7FB" };
const EMPTY_SUMMARY = { total: 0, active: 0, pending: 0, suspended: 0, deleted: 0 };

function formatDate(value, withTime = false) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    ...(withTime ? { timeStyle: "short" } : {}),
  }).format(date);
}

function formatMoney(value, currency = "ARS") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: currency || "ARS",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function customerInitials(customer) {
  const source = String(customer?.name || customer?.email || "C").trim();
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "C";
}

function statusMeta(status) {
  const value = String(status || "active");
  if (value === "suspended") return { label: "Suspendido", color: "error" };
  if (value === "pending") return { label: "Pendiente", color: "warning" };
  if (value === "deleted") return { label: "Eliminado", color: "default" };
  return { label: "Activo", color: "success" };
}

function priceListLabel(priceList) {
  if (!priceList || priceList.isBase) return "Lista base";
  if (priceList.status === "inactive") return `${priceList.name} (inactiva)`;
  if (priceList.status === "missing") return `${priceList.name} (no disponible)`;
  return priceList.name;
}

function DetailValue({ label, children }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: "rgba(25,36,47,.5)", fontWeight: 850 }}>
        {label}
      </Typography>
      <Typography sx={{ color: BRAND.dark, fontWeight: 750, overflowWrap: "anywhere" }}>
        {children || "—"}
      </Typography>
    </Box>
  );
}

function PriceListSelect({ customer, priceLists, disabled, onChange, compact = false }) {
  const current = customer?.priceList?.id || "base";
  const options = current !== "base" && !priceLists.some((list) => list.id === current)
    ? [{
        id: current,
        name: customer?.priceList?.name || "Lista no disponible",
        active: false,
        status: customer?.priceList?.status || "missing",
        isBase: false,
      }, ...priceLists]
    : priceLists;
  return (
    <FormControl size="small" fullWidth>
      <Select
        value={current}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        inputProps={{ "aria-label": `Lista de precios de ${customer?.name || customer?.email || "cliente"}` }}
        sx={{ minWidth: compact ? 170 : 220, bgcolor: "#fff", fontSize: compact ? ".78rem" : ".875rem" }}
      >
        {options.map((list) => (
          <MenuItem
            key={list.id}
            value={list.id}
            disabled={!list.active && list.id !== current}
          >
            {priceListLabel(list)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function CustomerMobileCard({
  customer,
  priceLists,
  canManageStatus,
  canManagePricing,
  busy,
  onOpen,
  onPriceList,
  onStatus,
}) {
  const status = statusMeta(customer.status);
  return (
    <Card variant="outlined" sx={{ borderColor: "rgba(25,36,47,.09)" }}>
      <CardContent>
        <Stack direction="row" spacing={1.2} alignItems="flex-start">
          <Avatar sx={{ bgcolor: BRAND.orange, fontWeight: 950 }}>{customerInitials(customer)}</Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{customer.name || "Sin nombre"}</Typography>
            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)", overflowWrap: "anywhere" }}>
              {customer.email}
            </Typography>
          </Box>
          <Stack spacing={.6} alignItems="flex-end">
            <Chip size="small" color={status.color} label={status.label} sx={{ fontWeight: 850 }} />
            {customer.origin === "manual" ? <Chip size="small" variant="outlined" label="Carga manual" /> : null}
          </Stack>
        </Stack>

        <Grid container spacing={1.2} sx={{ mt: .4 }}>
          <Grid item xs={6}><DetailValue label="Alta">{formatDate(customer.createdAt)}</DetailValue></Grid>
          <Grid item xs={6}><DetailValue label="Último acceso">{formatDate(customer.lastLoginAt, true)}</DetailValue></Grid>
          <Grid item xs={6}><DetailValue label="Pedidos">{customer.activity?.orderCount || 0}</DetailValue></Grid>
          <Grid item xs={6}><DetailValue label="Actividad">{formatDate(customer.lastActivityAt, true)}</DetailValue></Grid>
        </Grid>

        <Stack spacing={1} sx={{ mt: 1.5 }}>
          <PriceListSelect
            customer={customer}
            priceLists={priceLists}
            disabled={!canManagePricing || busy}
            onChange={(value) => onPriceList(customer, value)}
          />
          <Stack direction="row" spacing={1}>
            <Button fullWidth variant="outlined" onClick={() => onOpen(customer)} startIcon={<OpenInNewRoundedIcon />}>
              Ver ficha
            </Button>
            {customer.status !== "deleted" && canManageStatus ? (
              <Button
                fullWidth
                color={customer.status === "suspended" ? "success" : "error"}
                variant="outlined"
                disabled={busy}
                onClick={() => onStatus(customer)}
              >
                {customer.status === "suspended" ? "Reactivar" : "Suspender"}
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function CustomerDetailDialog({
  open,
  loading,
  detail,
  priceLists,
  canManageStatus,
  canManagePricing,
  busy,
  onClose,
  onPriceList,
  onStatus,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const customer = detail?.customer;
  const status = statusMeta(customer?.status);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle sx={{ pr: 7 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar sx={{ bgcolor: BRAND.orange, fontWeight: 950 }}>{customerInitials(customer)}</Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>
              {customer?.name || "Ficha del cliente"}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.55)", overflowWrap: "anywhere" }}>
              {customer?.email || ""}
            </Typography>
          </Box>
          {customer ? <Chip size="small" color={status.color} label={status.label} sx={{ fontWeight: 850 }} /> : null}
        </Stack>
        <IconButton aria-label="Cerrar ficha" onClick={onClose} disabled={busy} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 260 }}><CircularProgress /></Stack>
        ) : !customer ? (
          <Alert severity="error">No se pudo cargar la ficha del cliente.</Alert>
        ) : (
          <Stack spacing={2.2}>
            <Paper variant="outlined" sx={{ p: 2, borderColor: "rgba(25,36,47,.09)" }}>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark, mb: 1.4 }}>Contacto y registro</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}><DetailValue label="Email">{customer.email}</DetailValue></Grid>
                <Grid item xs={12} sm={6}><DetailValue label="Teléfono">{customer.phone}</DetailValue></Grid>
                <Grid item xs={12} sm={6}><DetailValue label="Documento">{customer.document}</DetailValue></Grid>
                <Grid item xs={12} sm={6}><DetailValue label="Fecha de registro">{formatDate(customer.createdAt, true)}</DetailValue></Grid>
                <Grid item xs={12} sm={6}><DetailValue label="Último acceso">{formatDate(customer.lastLoginAt, true)}</DetailValue></Grid>
                <Grid item xs={12} sm={6}><DetailValue label="Última actividad">{formatDate(customer.lastActivityAt, true)}</DetailValue></Grid>
              </Grid>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                {customer.origin === "manual" ? (
                  <Chip size="small" variant="outlined" label="Carga manual · sin acceso web" />
                ) : (
                  <Chip size="small" variant="outlined" label="Cliente con acceso web" />
                )}
                <Chip
                  size="small"
                  icon={<MailOutlineRoundedIcon />}
                  label={customer.marketing?.acceptsEmail ? "Acepta emails" : "No acepta emails"}
                  color={customer.marketing?.acceptsEmail ? "success" : "default"}
                />
                <Chip
                  size="small"
                  icon={<WhatsAppIcon />}
                  label={customer.marketing?.acceptsWhatsapp ? "Acepta WhatsApp" : "No acepta WhatsApp"}
                  color={customer.marketing?.acceptsWhatsapp ? "success" : "default"}
                />
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderColor: "rgba(25,36,47,.09)" }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} justifyContent="space-between">
                <Box>
                  <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Lista de precios personal</Typography>
                  <Typography variant="body2" sx={{ color: "rgba(25,36,47,.55)" }}>
                    El cambio queda vigente para el próximo acceso o compra del cliente.
                  </Typography>
                </Box>
                <Box sx={{ minWidth: { sm: 280 } }}>
                  <PriceListSelect
                    customer={customer}
                    priceLists={priceLists}
                    disabled={!canManagePricing || busy}
                    onChange={(value) => onPriceList(customer, value)}
                  />
                </Box>
              </Stack>
              {customer.priceList && !customer.priceList.active ? (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  La lista asignada está inactiva o ya no está disponible. El cliente puede volver a la lista base o recibir otra lista activa.
                </Alert>
              ) : null}
              {!canManagePricing ? <Alert severity="info" sx={{ mt: 1.5 }}>Tenés acceso de lectura, pero no permiso para modificar listas de precios.</Alert> : null}
            </Paper>

            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: "100%", borderColor: "rgba(25,36,47,.09)" }}>
                  <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Actividad comercial</Typography>
                  <Grid container spacing={1.2} sx={{ mt: .2 }}>
                    <Grid item xs={6}><DetailValue label="Pedidos">{customer.activity?.orderCount || 0}</DetailValue></Grid>
                    <Grid item xs={6}><DetailValue label="Valor registrado">{formatMoney(customer.activity?.orderValue, customer.activity?.orderCurrency)}</DetailValue></Grid>
                    <Grid item xs={6}><DetailValue label="Turnos">{customer.activity?.appointmentCount || 0}</DetailValue></Grid>
                    <Grid item xs={6}><DetailValue label="Último pedido">{formatDate(customer.activity?.lastOrderAt, true)}</DetailValue></Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: "100%", borderColor: "rgba(25,36,47,.09)" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Estado del cliente</Typography>
                      <Typography variant="body2" sx={{ color: "rgba(25,36,47,.55)" }}>Suspender conserva pedidos, turnos, precios e historial.</Typography>
                    </Box>
                    {canManageStatus && customer.status !== "deleted" ? (
                      <Button
                        variant="outlined"
                        color={customer.status === "suspended" ? "success" : "error"}
                        disabled={busy}
                        startIcon={customer.status === "suspended" ? <CheckCircleRoundedIcon /> : <BlockRoundedIcon />}
                        onClick={() => onStatus(customer)}
                      >
                        {customer.status === "suspended" ? "Reactivar" : "Suspender"}
                      </Button>
                    ) : null}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            {Array.isArray(customer.addresses) && customer.addresses.length ? (
              <Paper variant="outlined" sx={{ p: 2, borderColor: "rgba(25,36,47,.09)" }}>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark, mb: 1 }}>Direcciones guardadas</Typography>
                <Stack spacing={1}>
                  {customer.addresses.map((address, index) => (
                    <Box key={address.id || address._id || index} sx={{ p: 1.2, borderRadius: 2, bgcolor: "rgba(25,36,47,.035)" }}>
                      <Typography sx={{ fontWeight: 850 }}>{address.label || `Dirección ${index + 1}`}</Typography>
                      <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>
                        {[address.street, address.number, address.city, address.province, address.postalCode].filter(Boolean).join(", ") || "Sin detalle"}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ) : null}

            <Paper variant="outlined" sx={{ p: 2, borderColor: "rgba(25,36,47,.09)" }}>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark, mb: 1 }}>Pedidos recientes</Typography>
              {!detail.recentOrders?.length ? (
                <Typography variant="body2" sx={{ color: "rgba(25,36,47,.55)" }}>Este cliente todavía no tiene pedidos registrados.</Typography>
              ) : (
                <Stack divider={<Divider flexItem />}>
                  {detail.recentOrders.map((order) => (
                    <Stack key={order.id} direction="row" justifyContent="space-between" spacing={1.5} sx={{ py: 1 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 850 }}>{order.orderNumber || order.id}</Typography>
                        <Typography variant="caption" sx={{ color: "rgba(25,36,47,.5)" }}>{formatDate(order.createdAt, true)} · {order.status}</Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 900 }}>{formatMoney(order.total, order.currency)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Paper>

            {(customer.priceListHistory?.length || customer.statusHistory?.length) ? (
              <Paper variant="outlined" sx={{ p: 2, borderColor: "rgba(25,36,47,.09)" }}>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark, mb: 1 }}>Auditoría de cambios</Typography>
                <Stack spacing={1}>
                  {[...(customer.priceListHistory || []).map((item) => ({ ...item, kind: "price" })), ...(customer.statusHistory || []).map((item) => ({ ...item, kind: "status" }))]
                    .sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0))
                    .slice(0, 20)
                    .map((item, index) => (
                      <Box key={item.id || item._id || `${item.kind}-${index}`} sx={{ p: 1.1, borderRadius: 2, bgcolor: "rgba(25,36,47,.035)" }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {item.kind === "price"
                            ? `${item.fromPriceListName || "Lista base"} → ${item.toPriceListName || "Lista base"}`
                            : `${item.fromStatus || "—"} → ${item.toStatus || "—"}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(25,36,47,.5)" }}>
                          {formatDate(item.at, true)} · {item.actor?.name || item.actor?.email || "Sistema"}{item.reason ? ` · ${item.reason}` : ""}
                        </Typography>
                      </Box>
                    ))}
                </Stack>
              </Paper>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={busy}>Cerrar</Button></DialogActions>
    </Dialog>
  );
}

function ManualCustomerDialog({ open, busy, error, onClose, onSubmit }) {
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", document: "" });

  React.useEffect(() => {
    if (open) setForm({ name: "", email: "", phone: "", document: "" });
  }, [open]);

  const change = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const valid = form.name.trim() && form.email.trim();

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={(event) => { event.preventDefault(); if (valid && !busy) onSubmit(form); }}>
        <DialogTitle sx={{ fontWeight: 950 }}>Agregar cliente</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: .5 }}>
            <Alert severity="info">
              Se crea en el CRM sin contraseña ni acceso web. Más adelante puede vincularse su acceso sin duplicar la ficha del cliente.
            </Alert>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
              autoFocus
              required
              fullWidth
              label="Nombre completo"
              value={form.name}
              onChange={change("name")}
              inputProps={{ maxLength: 180 }}
            />
            <TextField
              required
              fullWidth
              type="email"
              label="Email"
              value={form.email}
              onChange={change("email")}
              inputProps={{ maxLength: 220 }}
            />
            <Grid container spacing={1.2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Teléfono / WhatsApp"
                  value={form.phone}
                  onChange={change("phone")}
                  inputProps={{ maxLength: 80 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Documento"
                  value={form.document}
                  onChange={change("document")}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={!valid || busy} startIcon={busy ? <CircularProgress size={18} /> : <PersonAddAlt1RoundedIcon />}>
            Crear cliente
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default function ProjectCustomersPage() {
  const { projectId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { activeProject, projects, setActiveProject } = useProjects();
  const [memberAccess, setMemberAccess] = React.useState(normalizeProjectAccess());
  const [accessLoading, setAccessLoading] = React.useState(true);
  const [customers, setCustomers] = React.useState([]);
  const [priceLists, setPriceLists] = React.useState([{ id: "base", name: "Lista base", active: true, status: "active", isBase: true }]);
  const [summary, setSummary] = React.useState(EMPTY_SUMMARY);
  const [pagination, setPagination] = React.useState({ page: 1, pageSize: 25, total: 0, pages: 1 });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [status, setStatus] = React.useState("all");
  const [preference, setPreference] = React.useState("all");
  const [priceListFilter, setPriceListFilter] = React.useState("all");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detail, setDetail] = React.useState(null);
  const [busyCustomerId, setBusyCustomerId] = React.useState("");
  const [statusPrompt, setStatusPrompt] = React.useState(null);
  const [statusReason, setStatusReason] = React.useState("");
  const [createOpen, setCreateOpen] = React.useState(false);
  const [createBusy, setCreateBusy] = React.useState(false);
  const [createError, setCreateError] = React.useState("");
  const requestRef = React.useRef(0);

  const canRead = hasProjectPermission(memberAccess, "customers.read");
  const canManageStatus = hasProjectPermission(memberAccess, "customers.manage");
  const canCreateCustomer = hasProjectPermission(memberAccess, "customers.manage");
  const canManagePricing = hasProjectPermission(memberAccess, "customers.pricing.manage");
  const effectiveProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId || item._id === projectId) || activeProject,
    [activeProject, projectId, projects],
  );

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  React.useEffect(() => {
    let active = true;
    setAccessLoading(true);
    setError("");
    setCustomers([]);
    setDetail(null);
    setCreateOpen(false);
    setCreateError("");
    setPage(0);

    Promise.all([projectsApi.get(projectId), customersApi.priceLists(projectId)])
      .then(([projectData, priceListData]) => {
        if (!active) return;
        setMemberAccess(normalizeProjectAccess(projectData.memberAccess || projectData.member || {}));
        setPriceLists(priceListData.priceLists?.length ? priceListData.priceLists : [{ id: "base", name: "Lista base", active: true, status: "active", isBase: true }]);
        if (projectData.project) setActiveProject(projectData.project);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || "No se pudo abrir el CRM de este negocio.");
      })
      .finally(() => {
        if (active) setAccessLoading(false);
      });

    return () => { active = false; };
  }, [projectId, setActiveProject]);

  const loadCustomers = React.useCallback(async () => {
    if (!projectId || !canRead) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    setError("");
    try {
      const data = await customersApi.list(projectId, {
        page: page + 1,
        pageSize,
        q: search,
        status: status === "all" ? "" : status,
        preference: preference === "all" ? "" : preference,
        priceListId: priceListFilter === "all" ? "" : priceListFilter,
      });
      if (requestId !== requestRef.current) return;
      setCustomers(data.customers || []);
      setSummary(data.summary || EMPTY_SUMMARY);
      setPagination(data.pagination || { page: page + 1, pageSize, total: 0, pages: 1 });
    } catch (err) {
      if (requestId !== requestRef.current) return;
      setCustomers([]);
      setError(err.message || "No se pudieron cargar los clientes.");
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [canRead, page, pageSize, preference, priceListFilter, projectId, search, status]);

  React.useEffect(() => {
    if (!accessLoading && canRead) loadCustomers();
  }, [accessLoading, canRead, loadCustomers]);

  const openCustomer = async (customer) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail({ customer });
    setError("");
    try {
      const data = await customersApi.get(projectId, customer.id);
      setDetail(data);
      if (data.priceLists?.length) setPriceLists(data.priceLists);
    } catch (err) {
      setError(err.message || "No se pudo cargar la ficha del cliente.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async (customerId) => {
    if (!detailOpen || detail?.customer?.id !== customerId) return;
    const data = await customersApi.get(projectId, customerId);
    setDetail(data);
    if (data.priceLists?.length) setPriceLists(data.priceLists);
  };

  const createManualCustomer = async (payload) => {
    if (!canCreateCustomer) return;
    setCreateBusy(true);
    setCreateError("");
    setError("");
    setSuccess("");
    try {
      const data = await customersApi.create(projectId, payload);
      setCreateOpen(false);
      setSuccess(data.message || "Cliente creado correctamente.");
      await loadCustomers();
      if (data.customer?.id) await openCustomer(data.customer);
    } catch (err) {
      setCreateError(err.message || "No se pudo crear el cliente.");
    } finally {
      setCreateBusy(false);
    }
  };

  const assignPriceList = async (customer, nextPriceListId) => {
    if (!canManagePricing || !customer?.id) return;
    setBusyCustomerId(customer.id);
    setError("");
    setSuccess("");
    try {
      const data = await customersApi.assignPriceList(
        projectId,
        customer.id,
        nextPriceListId === "base" ? null : nextPriceListId,
        customer.updatedAt,
      );
      setSuccess(data.message || "Lista de precios actualizada.");
      await Promise.all([loadCustomers(), refreshDetail(customer.id)]);
    } catch (err) {
      setError(err.message || "No se pudo actualizar la lista de precios.");
      if (err?.status === 409) {
        await Promise.all([loadCustomers(), refreshDetail(customer.id).catch(() => {})]);
      }
    } finally {
      setBusyCustomerId("");
    }
  };

  const askStatusChange = (customer) => {
    setStatusReason("");
    setStatusPrompt({
      customer,
      nextStatus: customer.status === "suspended" ? "active" : "suspended",
    });
  };

  const confirmStatusChange = async () => {
    const customer = statusPrompt?.customer;
    if (!customer || !canManageStatus) return;
    setBusyCustomerId(customer.id);
    setError("");
    setSuccess("");
    try {
      const data = await customersApi.updateStatus(
        projectId,
        customer.id,
        statusPrompt.nextStatus,
        statusReason,
        customer.updatedAt,
      );
      setSuccess(data.message || "Estado actualizado.");
      setStatusPrompt(null);
      await Promise.all([loadCustomers(), refreshDetail(customer.id)]);
    } catch (err) {
      setError(err.message || "No se pudo actualizar el estado del cliente.");
      if (err?.status === 409) {
        setStatusPrompt(null);
        await Promise.all([loadCustomers(), refreshDetail(customer.id).catch(() => {})]);
      }
    } finally {
      setBusyCustomerId("");
    }
  };

  if (accessLoading) {
    return <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}><CircularProgress /></Stack>;
  }

  if (!canRead) {
    return (
      <Alert severity="warning">
        No tenés permiso para ver los clientes registrados de este negocio. Solicitá el permiso <strong>customers.read</strong> a un administrador.
      </Alert>
    );
  }

  return (
    <Stack spacing={2.2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: "center" }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <GroupsRoundedIcon sx={{ color: BRAND.orange }} />
            <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>CRM de clientes</Typography>
          </Stack>
          <Typography sx={{ color: "rgba(25,36,47,.58)", mt: .5 }}>
            Clientes de {effectiveProject?.name || "este negocio"}, cargados desde la web o manualmente.
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
          {canCreateCustomer ? (
            <Button variant="contained" startIcon={<PersonAddAlt1RoundedIcon />} onClick={() => { setCreateError(""); setCreateOpen(true); }}>
              Agregar cliente
            </Button>
          ) : null}
          <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={loadCustomers} disabled={loading}>
            Actualizar
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      {success ? <Alert severity="success" onClose={() => setSuccess("")}>{success}</Alert> : null}

      <Grid container spacing={1.5}>
        {[
          ["Total", summary.total, "default"],
          ["Activos", summary.active, "success"],
          ["Pendientes", summary.pending, "warning"],
          ["Suspendidos", summary.suspended, "error"],
        ].map(([label, value, color]) => (
          <Grid item xs={6} md={3} key={label}>
            <Card variant="outlined" sx={{ borderColor: "rgba(25,36,47,.08)" }}>
              <CardContent>
                <Typography variant="caption" sx={{ color: "rgba(25,36,47,.5)", fontWeight: 900 }}>{label}</Typography>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>{value}</Typography>
                  <Chip size="small" color={color} label={label} sx={{ display: { xs: "none", sm: "inline-flex" }, fontWeight: 850 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ p: 1.6, borderColor: "rgba(25,36,47,.08)" }}>
        <Grid container spacing={1.2}>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Buscar por nombre, email, teléfono o documento"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2.2}>
            <TextField select fullWidth size="small" label="Estado" value={status} onChange={(event) => { setStatus(event.target.value); setPage(0); }}>
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="active">Activos</MenuItem>
              <MenuItem value="pending">Pendientes</MenuItem>
              <MenuItem value="suspended">Suspendidos</MenuItem>
              <MenuItem value="deleted">Eliminados</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={2.2}>
            <TextField select fullWidth size="small" label="Preferencias" value={preference} onChange={(event) => { setPreference(event.target.value); setPage(0); }}>
              <MenuItem value="all">Todas</MenuItem>
              <MenuItem value="email">Acepta emails</MenuItem>
              <MenuItem value="whatsapp">Acepta WhatsApp</MenuItem>
              <MenuItem value="none">Sin preferencias</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={2.6}>
            <TextField select fullWidth size="small" label="Lista de precios" value={priceListFilter} onChange={(event) => { setPriceListFilter(event.target.value); setPage(0); }}>
              <MenuItem value="all">Todas</MenuItem>
              <MenuItem value="base">Lista base / sin personal</MenuItem>
              <MenuItem value="inactive">Asignación inactiva</MenuItem>
              {priceLists.filter((list) => !list.isBase).map((list) => (
                <MenuItem key={list.id} value={list.id}>{priceListLabel(list)}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {loading && !customers.length ? (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 280 }}><CircularProgress /></Stack>
      ) : !customers.length ? (
        <Alert severity="info">
          {summary.total === 0
            ? "Este negocio todavía no tiene clientes. Podés cargar el primero manualmente o recibirlo desde la web pública."
            : "No hay clientes que coincidan con los filtros actuales."}
        </Alert>
      ) : isMobile ? (
        <Stack spacing={1.3}>
          {customers.map((customer) => (
            <CustomerMobileCard
              key={customer.id}
              customer={customer}
              priceLists={priceLists}
              canManageStatus={canManageStatus}
              canManagePricing={canManagePricing}
              busy={busyCustomerId === customer.id}
              onOpen={openCustomer}
              onPriceList={assignPriceList}
              onStatus={askStatusChange}
            />
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: "rgba(25,36,47,.08)" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "rgba(25,36,47,.035)" }}>
                <TableCell>Cliente</TableCell>
                <TableCell>Contacto</TableCell>
                <TableCell>Registro / acceso</TableCell>
                <TableCell>Actividad</TableCell>
                <TableCell sx={{ minWidth: 210 }}>Lista de precios</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map((customer) => {
                const statusInfo = statusMeta(customer.status);
                const busy = busyCustomerId === customer.id;
                return (
                  <TableRow key={customer.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.1} alignItems="center">
                        <Avatar sx={{ width: 36, height: 36, bgcolor: BRAND.orange, fontWeight: 950, fontSize: 13 }}>{customerInitials(customer)}</Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{customer.name || "Sin nombre"}</Typography>
                          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.48)" }}>{customer.document || "Sin documento"}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={.3}>
                        <Stack direction="row" spacing={.6} alignItems="center"><MailOutlineRoundedIcon sx={{ fontSize: 15, color: "rgba(25,36,47,.45)" }} /><Typography variant="body2">{customer.email}</Typography></Stack>
                        <Stack direction="row" spacing={.6} alignItems="center"><PhoneRoundedIcon sx={{ fontSize: 15, color: "rgba(25,36,47,.45)" }} /><Typography variant="body2">{customer.phone || "—"}</Typography></Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 750 }}>{formatDate(customer.createdAt)}</Typography>
                      <Typography variant="caption" sx={{ color: "rgba(25,36,47,.48)" }}>
                        {customer.webAccess?.enabled === false ? "Carga manual · sin acceso web" : `Acceso ${formatDate(customer.lastLoginAt, true)}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={.7} alignItems="center"><ShoppingBagRoundedIcon sx={{ fontSize: 17, color: BRAND.orange }} /><Typography sx={{ fontWeight: 850 }}>{customer.activity?.orderCount || 0} pedidos</Typography></Stack>
                      <Typography variant="caption" sx={{ color: "rgba(25,36,47,.48)" }}>Última {formatDate(customer.lastActivityAt, true)}</Typography>
                    </TableCell>
                    <TableCell>
                      <PriceListSelect
                        compact
                        customer={customer}
                        priceLists={priceLists}
                        disabled={!canManagePricing || busy}
                        onChange={(value) => assignPriceList(customer, value)}
                      />
                      {customer.priceList && !customer.priceList.active ? <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 800 }}>Requiere reasignación</Typography> : null}
                    </TableCell>
                    <TableCell><Chip size="small" color={statusInfo.color} label={statusInfo.label} sx={{ fontWeight: 850 }} /></TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={.5} justifyContent="flex-end">
                        <Tooltip title="Abrir ficha"><IconButton size="small" onClick={() => openCustomer(customer)}><OpenInNewRoundedIcon fontSize="small" /></IconButton></Tooltip>
                        {canManageStatus && customer.status !== "deleted" ? (
                          <Tooltip title={customer.status === "suspended" ? "Reactivar cuenta" : "Suspender cuenta"}>
                            <span>
                              <IconButton size="small" color={customer.status === "suspended" ? "success" : "error"} disabled={busy} onClick={() => askStatusChange(customer)}>
                                {customer.status === "suspended" ? <CheckCircleRoundedIcon fontSize="small" /> : <BlockRoundedIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={pagination.total || 0}
            page={page}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por página"
          />
        </TableContainer>
      )}

      {isMobile && customers.length ? (
        <TablePagination
          component="div"
          count={pagination.total || 0}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Por página"
        />
      ) : null}

      <ManualCustomerDialog
        open={createOpen}
        busy={createBusy}
        error={createError}
        onClose={() => { if (!createBusy) { setCreateOpen(false); setCreateError(""); } }}
        onSubmit={createManualCustomer}
      />

      <CustomerDetailDialog
        open={detailOpen}
        loading={detailLoading}
        detail={detail}
        priceLists={priceLists}
        canManageStatus={canManageStatus}
        canManagePricing={canManagePricing}
        busy={Boolean(busyCustomerId)}
        onClose={() => { setDetailOpen(false); setDetail(null); }}
        onPriceList={assignPriceList}
        onStatus={askStatusChange}
      />

      <Dialog open={Boolean(statusPrompt)} onClose={busyCustomerId ? undefined : () => setStatusPrompt(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 950 }}>
          {statusPrompt?.nextStatus === "suspended" ? "Suspender cliente" : "Reactivar cliente"}
        </DialogTitle>
        <DialogContent>
          <Alert severity={statusPrompt?.nextStatus === "suspended" ? "warning" : "info"} sx={{ mb: 1.5 }}>
            {statusPrompt?.nextStatus === "suspended"
              ? "El cliente quedará suspendido en el CRM y, si tiene acceso web, tampoco podrá iniciar sesión. Su historial se conserva."
              : "El cliente volverá a estar activo en el CRM y, si tiene acceso web, podrá usarlo normalmente."}
          </Alert>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Motivo interno (opcional)"
            value={statusReason}
            onChange={(event) => setStatusReason(event.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusPrompt(null)} disabled={Boolean(busyCustomerId)}>Cancelar</Button>
          <Button
            variant="contained"
            color={statusPrompt?.nextStatus === "suspended" ? "error" : "success"}
            onClick={confirmStatusChange}
            disabled={Boolean(busyCustomerId)}
          >
            {busyCustomerId ? <CircularProgress size={20} /> : statusPrompt?.nextStatus === "suspended" ? "Suspender" : "Reactivar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
